import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './Button';

interface NavLink {
  href: string;
  label: string;
}

interface HeaderProps {
  navLinks: NavLink[];
}

export const Header: React.FC<HeaderProps> = ({ navLinks }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="header"
      data-scrolled={isScrolled}
      className={`
        sticky top-0 z-50 transition-all duration-150 ease-out
        ${isScrolled || isMenuOpen
          ? 'bg-[#FAFAF8]/95 backdrop-blur-xl border-b border-[#E8E6E2]'
          : 'bg-transparent'
        }
      `}
    >
      <div className="h-20 max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between relative">
        {/* Hamburger — left */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-2 -ml-2 text-[#404040]"
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          aria-controls="site-menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            {isMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5M3.75 15h16.5" />
            )}
          </svg>
        </button>

        {/* Logo — centered */}
        <Link
          to="/"
          className="absolute left-1/2 -translate-x-1/2 font-display font-bold uppercase tracking-[0.08em] text-lg sm:text-xl md:text-2xl text-[#404040] whitespace-nowrap"
        >
          Lot 20 Coffee
        </Link>

        {/* CTA — right */}
        <div className="hidden sm:block">
          <Button to="/contact" variant="primary" size="sm">Get in Touch</Button>
        </div>
        <Link
          to="/contact"
          className="sm:hidden p-2 -mr-2 text-[#E85C27]"
          aria-label="Get in touch"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
            <path d="M12 2.25c.66 3.98 1.77 6.4 3.75 8.25.98.92 2.1 1.64 3.5 2.25a.75.75 0 010 1.5c-1.4.6-2.52 1.33-3.5 2.25-1.98 1.85-3.09 4.27-3.75 8.25-.66-3.98-1.77-6.4-3.75-8.25C7.27 15.58 6.15 14.85 4.75 14.25a.75.75 0 010-1.5c1.4-.6 2.52-1.33 3.5-2.25C10.23 8.65 11.34 6.23 12 2.25z" />
          </svg>
        </Link>
      </div>

      {/* Menu — hamburger-triggered, all breakpoints */}
      <div
        id="site-menu"
        className={`
          overflow-hidden transition-all duration-300 ease-out bg-[#FAFAF8]
          ${isMenuOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'}
        `}
        aria-hidden={!isMenuOpen}
      >
        <nav className="px-6 pb-8 pt-2 max-w-7xl mx-auto space-y-1" aria-label="Main navigation">
          {navLinks.map(link => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsMenuOpen(false)}
                className={`
                  block py-3 text-sm uppercase tracking-[0.1em] font-medium
                  border-b border-[#E8E6E2] last:border-0
                  transition-colors duration-200
                  ${isActive ? 'text-[#404040]' : 'text-[#6B6B6B]'}
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                {link.label}
              </Link>
            );
          })}
          <div className="pt-4 sm:hidden">
            <Button
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              variant="primary"
              size="lg"
              className="w-full"
            >
              Get in Touch
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};
